import { useSelector } from 'react-redux';
import cx from 'classnames';

// Types
import { OrderDetailsProps } from './OrderDetails.types';

// Config
import orderDetailsState from './OrderDetails.state';

// Components
import AdditionalDeliveryInfo from '@components/additional-delivery-info';
import AppSurvey from '@components/app-survey';
import BodyText from '@components/body-text';
import Container from '@components/container';
import DisplayAddress from '@components/display-address/DisplayAddress';
import DisplayDeliveryDetails from '@components/display-delivery-details';
import EditDetailsLink from '@components/edit-details-link';
import Heading from '@components/heading';
import OrderPersonalisation from '@components/order-personalisation';
import UserEmailAddress from '@components/user-email-address';

// Styles
import styles from './order-details.scss';

const OrderDetails = ({
  collectionAddressTitle = '',
  collectionDetailsTitle = '',
  deliveryDetailsTitle = '',
  displayAddressDataTest = '',
  displayAddressTitle = '',
  displayBillingAddressDataTest = '',
  hideDeliveryDetails = false,
  isPaymentPage = false,
  openMoreDetailsModal = () => {},
  orderDetails = {},
  showEditDetailsLink = false,
}: OrderDetailsProps) => {
  const { selectedCollectionPoint, showOrderPersonalisation, showSurvey } = useSelector(orderDetailsState);

  const {
    address,
    addressee,
    billingAddress,
    deliveryInstructions,
    giftMessageLines = [],
    isClickCollectOrder,
    phoneNumber,
  } = orderDetails ?? {};

  return (
    <>
      <Container
        background="white"
        className={cx(styles.container, {
          [styles.addPadding]: !isPaymentPage,
        })}
        data-monetate="order-details-container"
        data-testid="order-details-container"
      >
        {showOrderPersonalisation && <OrderPersonalisation />}
        <div
          className={cx(styles.wrapper, {
            [styles.useGrid]: !hideDeliveryDetails,
          })}
          data-testid="order-details-wrapper"
        >
          <div>
            <DisplayAddress
              address={address}
              addressee={addressee}
              collectionAddressTitle={collectionAddressTitle}
              data-testid={displayAddressDataTest}
              displayAddressTitle={displayAddressTitle}
              isClickCollectOrder={isClickCollectOrder}
              openMoreDetailsModal={openMoreDetailsModal}
              phoneNumber={phoneNumber}
              renderPostcodeLast={isClickCollectOrder}
              selectedCollectionPoint={selectedCollectionPoint}
            />
          </div>

          {!hideDeliveryDetails && (
            <DisplayDeliveryDetails
              collectionDetailsTitle={collectionDetailsTitle}
              deliveryDetailsTitle={deliveryDetailsTitle}
              orderDetails={orderDetails}
            />
          )}

          {billingAddress && (
            <div className={styles.fullWidth}>
              <DisplayAddress
                address={billingAddress.address}
                addressee={billingAddress.addressee}
                collectionAddressTitle={collectionAddressTitle}
                data-testid={displayBillingAddressDataTest}
                displayAddressTitle="Billing address"
                inline
                phoneNumber={billingAddress.phoneNumber}
              />
            </div>
          )}

          {isClickCollectOrder && (
            <div data-testid="delivery-details-collection-phone-number-container">
              <Heading
                label="Contact number"
                marginBottom="1"
                tag="h3"
                testId="delivery-details-collection-phone-number-title"
                type="xs"
              />
              <BodyText maskText testId="delivery-details-collection-phone-number">
                {phoneNumber}
              </BodyText>
            </div>
          )}

          <UserEmailAddress headingTag="h3" noContainerTopMargin />

          {giftMessageLines.length > 0 && <AdditionalDeliveryInfo text={giftMessageLines} title="Gift message" />}

          {deliveryInstructions && (
            <AdditionalDeliveryInfo text={deliveryInstructions} title="Additional delivery instructions" />
          )}
        </div>

        {showEditDetailsLink && <EditDetailsLink />}
      </Container>

      {showSurvey && !isPaymentPage && <AppSurvey />}
    </>
  );
};

export default OrderDetails;
